import { Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { SnCamera } from '@lib/api/entities';

@Injectable({ providedIn: 'root' })
export class PtzControlsService {
  public ptzMovementModifier: number = 6.5;

  private _ptzMovement$ = new Subject<boolean>();
  private _openPresetsMenu$ = new Subject<boolean>();
  private _ptzMoved$ = new Subject<string>();

  private _id: string;
  private _host: any;
  private _username: string;
  private _password: string;
  private _path: string;
  private _onvifPort: number;
  private _urls: any;
  private _cameraMeta: string;
  private _make: string;
  private _accCameraId: string;
  private _sessionToken: string;
  private _isExternalPtz: boolean;
  private _external: any;

  constructor(private readonly _httpClient: HttpClient) {}

  get ptzMovement$(): Observable<boolean> {
    return this._ptzMovement$.asObservable();
  }

  get openPresetsMenu$(): Observable<boolean> {
    return this._openPresetsMenu$.asObservable();
  }

  get ptzMoved$(): Observable<string> {
    return this._ptzMoved$.asObservable();
  }

  get isExternalPtzAvigilon(): boolean {
    return this._isExternalPtz && this._external?.externalType?.toLowerCase() === 'avigilon';
  }

  public ptzMovement(value: boolean): void {
    this._ptzMovement$.next(value);
  }

  public openPresetsMenu(): void {
    this._openPresetsMenu$.next(true);
  }

  public setConfig(config: any, local?: boolean): void {
    this._urls = local ? config.vendorApis?.local?.ptz : config.vendorApis?.cloud?.ptz;
    this._id = config.id;
    this._onvifPort = config.isExternalPtz ? config.external?.port : config.onvifPort;
    this._host = config.isExternalPtz ? config.external?.host : config.host;
    this._username = config.username;
    this._password = config.password;
    this._path = config.path;
    this._cameraMeta = config.cameraMeta;
    this._make = config.make;
    this._accCameraId = config.external?.externalCameraId;
    this._sessionToken = config.external?.externalMeta?.sessionToken;
    this._isExternalPtz = config.isExternalPtz;
    this._external = config.external;
  }

  public getPtz(): Observable<any> {
    const data: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      path: this._path,
      cameraMeta: this._cameraMeta,
    };
    if (this.isExternalPtzAvigilon) {
      data.accCameraId = this._accCameraId;
      data.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzInfo;
    return url ? this._httpClient.post(url, data) : of(null);
  }

  public getPtzList(pageIndex?: number): Observable<any> {
    const data: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      page: pageIndex ? pageIndex : 0,
      path: this._path,
      cameraMeta: this._cameraMeta,
    };
    if (this.isExternalPtzAvigilon) {
      data.accCameraId = this._accCameraId;
      data.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzPresetList;
    return url
      ? this._httpClient.post(url, data).pipe(
          map((res: any) => {
            if (this.isExternalPtzAvigilon) {
              return res;
            }
            return res.result;
          }),
        )
      : of(null);
  }

  public centerTo(x: number, y: number, resolutionX: number, resolutionY: number): Observable<any> {
    const url = this._urls.ptzCenter;
    if (!url) {
      return of(null);
    }
    const payload: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      cameraMeta: this._cameraMeta,
      coordinates: {
        x: Math.ceil(x),
        y: Math.ceil(y),
      },
      resolution: {
        x: Math.ceil(resolutionX),
        y: Math.ceil(resolutionY),
      },
      path: this._path,
    };
    if (this.isExternalPtzAvigilon) {
      payload.accCameraId = this._accCameraId;
      payload.sessionToken = this._sessionToken;
    }
    return this._httpClient.post(url, payload).pipe(tap(() => this._ptzMoved$.next(this._id)));
  }

  public areaZoom(
    x?: number,
    y?: number,
    z?: number,
    resolutionX?: number,
    resolutionY?: number,
    local?: boolean,
    ptzConfig?: any,
    finalX?: number,
    finalY?: number,
    box?: any,
  ): Observable<any> {
    const url = this._urls.ptzAreazoom;
    const payload: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      cameraMeta: this._cameraMeta,
      resolution: {
        x: resolutionX,
        y: resolutionY,
      },
      width: resolutionX,
      height: resolutionY,
      path: this._path,
      zoomType: ptzConfig.isHanwha ? 'ZoomIn' : null,
      box: {
        x: this.isExternalPtzAvigilon ? box.width : resolutionX,
        y: this.isExternalPtzAvigilon ? box.height : resolutionY,
      },
    };
    payload.coordinates = ptzConfig.isSony
      ? {
          x: {
            fromCenter: x,
          },
          y: {
            fromCenter: y,
          },
        }
      : ptzConfig.isHanwha
      ? {
          x: {
            initial: x,
            final: finalX,
          },
          y: {
            initial: y,
            final: finalY,
          },
        }
      : ptzConfig.isBosch || ptzConfig.isIPro
      ? { x, y, xEnd: finalX, yEnd: finalY }
      : { x, y, z };
    if (this.isExternalPtzAvigilon) {
      payload.accCameraId = this._accCameraId;
      payload.sessionToken = this._sessionToken;
    }
    return url
      ? this._httpClient.post(url, payload).pipe(tap(() => this._ptzMoved$.next(this._id)))
      : of(null);
  }

  public movePtz(direction: any, amount: Number, movementType?: string): Observable<SnCamera> {
    const url =
      movementType === 'continuous' ? this._urls.ptzContinuousPantiltMove : this._urls.ptzMoveUrls;
    const movement = { x: 0, y: 0, z: 0 };
    movement[direction] = amount;
    const payload: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      cameraMeta: this._cameraMeta,
      coordinates: movement,
      movementType: movementType ? movementType : 'relative',
      path: this._path,
    };
    if (this.isExternalPtzAvigilon) {
      payload.accCameraId = this._accCameraId;
      payload.sessionToken = this._sessionToken;
    }

    return url
      ? this._httpClient.post(url, payload).pipe(
          tap(() => this._ptzMoved$.next(this._id)),
          map((response: any) => new SnCamera(response)),
        )
      : of(null);
  }

  public qognifyZoom(ptzConfig: any, ptzZoom: string): Observable<any> {
    if (ptzConfig.isQognify) {
      let url = this._urls.ptzMoveUrls;

      url +=
        '&cameraID=' +
        ptzConfig.cameraID +
        '&ptzDirection=NONE' +
        '&ptzZoom=' +
        ptzZoom +
        '&ptzSpeed=NONE';

      return url
        ? this._httpClient.get(url, {}).pipe(tap(() => this._ptzMoved$.next(this._id)))
        : of(null);
    }
  }

  public dragPtz(
    coordinates: { x: number; y: number; z: number },
    ptzConfig: any,
    movementType?: string,
    noModifier?: boolean,
  ): Observable<any> {
    if (ptzConfig.isQognify) {
      let url = this._urls.ptzMoveUrls;
      let ptzdirect;

      if (coordinates.x >= -0.005 && coordinates.x <= 0.005 && coordinates.y > 0.005) {
        ptzdirect = 'UP';
      }
      if (coordinates.x > 0.005 && coordinates.y > 0.005) {
        ptzdirect = 'UPRIGHT';
      }
      if (coordinates.x > 0.005 && coordinates.y >= -0.005 && coordinates.y <= 0.005) {
        ptzdirect = 'RIGHT';
      }
      if (coordinates.x > 0.005 && coordinates.y < -0.005) {
        ptzdirect = 'DOWNRIGHT';
      }
      if (coordinates.x >= -0.005 && coordinates.x <= 0.005 && coordinates.y < -0.005) {
        ptzdirect = 'DOWN';
      }
      if (coordinates.x < -0.005 && coordinates.y < -0.005) {
        ptzdirect = 'DOWNLEFT';
      }
      if (coordinates.x < -0.005 && coordinates.y >= -0.005 && coordinates.y <= 0.005) {
        ptzdirect = 'LEFT';
      }
      if (coordinates.x < -0.005 && coordinates.y > 0.005) {
        ptzdirect = 'UPLEFT';
      }
      if (coordinates.x === null && coordinates.y === null) {
        ptzdirect = 'NONE';
      }

      url +=
        '&cameraID=' +
        ptzConfig.cameraID +
        '&ptzDirection=' +
        ptzdirect +
        '&ptzZoom=NONE' +
        '&ptzSpeed=SLOW';

      return url
        ? this._httpClient.get(url, {}).pipe(tap(() => this._ptzMoved$.next(this._id)))
        : of(null);
    } else {
      const url =
        movementType === 'continuous' || movementType === 'stop'
          ? this._urls.ptzContinuousPantiltMove
          : this._urls.ptzMoveUrls;
      if (movementType === 'continuous' && !noModifier) {
        coordinates.x = coordinates.x * this.ptzMovementModifier;
        coordinates.y = coordinates.y * this.ptzMovementModifier;
      }
      const payload = {
        id: this._id,
        port: this._onvifPort,
        deviceIp: this._host,
        username: this._username,
        password: this._password,
        cameraMeta: this._cameraMeta,
        coordinates,
        movementType: movementType ? movementType : 'relative',
        path: this._path,
        accCameraId: null,
        sessionToken: null,
      };

      if (this.isExternalPtzAvigilon) {
        payload.accCameraId = this._accCameraId;
        payload.sessionToken = this._sessionToken;
      }

      return url
        ? this._httpClient.post(url, payload).pipe(tap(() => this._ptzMoved$.next(this._id)))
        : of(null);
    }
  }

  public createPreset(presetName: string, presetNumber?: number): Observable<any> {
    const preset: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      cameraMeta: this._cameraMeta,
      presetName,
      presetNumber,
      path: this._path,
    };
    if (this.isExternalPtzAvigilon) {
      preset.accCameraId = this._accCameraId;
      preset.sessionToken = this._sessionToken;
    }

    const url = this._urls.ptzPresetCreate;

    return url ? this._httpClient.post(url, preset) : of(null);
  }

  public deletePreset(presetNumber: string): Observable<any> {
    const preset: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      cameraMeta: this._cameraMeta,
      presetNumber,
      path: this._path,
    };
    if (this.isExternalPtzAvigilon) {
      preset.accCameraId = this._accCameraId;
      preset.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzPresetDelete;

    return url ? this._httpClient.post(url, preset) : of(null);
  }

  public editPreset(presetNumber: string, presetName: number): Observable<any> {
    const preset: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      cameraMeta: this._cameraMeta,
      presetNumber,
      presetName,
      path: this._path,
    };
    if (this.isExternalPtzAvigilon) {
      preset.accCameraId = this._accCameraId;
      preset.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzPresetCreate;

    return url ? this._httpClient.post(url, preset) : of(null);
  }

  public goToPreset(presetNumber: string): Observable<any> {
    const preset: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      cameraMeta: this._cameraMeta,
      presetNumber,
      movementType: 'goToPreset',
      path: this._path,
      coordinates: { x: 0, y: 0, z: 0 },
    };
    if (this.isExternalPtzAvigilon) {
      preset.accCameraId = this._accCameraId;
      preset.sessionToken = this._sessionToken;
    }

    const url = this._urls.ptzMoveUrls;

    return url
      ? this._httpClient.post(url, preset).pipe(
          tap(() => this._ptzMoved$.next(this._id)),
          map((response: any) => response.results),
        )
      : of(null);
  }

  public togglePtzAutotrack(on: ConstrainBoolean): Observable<any> {
    const data: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      path: this._path,
      cameraMeta: this._cameraMeta,
    };
    let url = this._urls.ptzAutotrackSet;
    if (this.isExternalPtzAvigilon) {
      data.accCameraId = this._accCameraId;
      data.sessionToken = this._sessionToken;
    }
    if (!on) {
      url = this._urls.ptzAutotrackRemove;
      return url ? this._httpClient.post(url, data) : of(null);
    }
    return url ? this._httpClient.post(url, data) : of(null);
  }

  public ptzFocusSet(): Observable<any> {
    const data: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      path: this._path,
      cameraMeta: this._cameraMeta,
    };
    if (this.isExternalPtzAvigilon) {
      data.accCameraId = this._accCameraId;
      data.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzFocusSet;
    return url ? this._httpClient.post(url, data) : of(null);
  }

  public ptzBacklightSet(): Observable<any> {
    const data: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      path: this._path,
      cameraMeta: this._cameraMeta,
    };
    if (this.isExternalPtzAvigilon) {
      data.accCameraId = this._accCameraId;
      data.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzBacklightSet;
    return url ? this._httpClient.post(url, data) : of(null);
  }

  public ptzBacklightRemove(): Observable<any> {
    const data: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      path: this._path,
      cameraMeta: this._cameraMeta,
    };
    if (this.isExternalPtzAvigilon) {
      data.accCameraId = this._accCameraId;
      data.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzBacklightRemove;
    return url ? this._httpClient.post(url, data) : of(null);
  }

  public ptzHomeMove(): Observable<any> {
    const data: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      movementType: 'relative',
      path: this._path,
      cameraMeta: this._cameraMeta,
    };
    if (this.isExternalPtzAvigilon) {
      data.accCameraId = this._accCameraId;
      data.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzHomeMove;
    return url
      ? this._httpClient.post(url, data).pipe(tap(() => this._ptzMoved$.next(this._id)))
      : of(null);
  }

  public ptzHomeSet(): Observable<any> {
    const data: any = {
      id: this._id,
      port: this._onvifPort,
      deviceIp: this._host,
      username: this._username,
      password: this._password,
      path: this._path,
      cameraMeta: this._cameraMeta,
    };
    if (this.isExternalPtzAvigilon) {
      data.accCameraId = this._accCameraId;
      data.sessionToken = this._sessionToken;
    }
    const url = this._urls.ptzHomeSet;
    return url ? this._httpClient.post(url, data) : of(null);
  }
}
